import type { RefObject } from 'react'

import {
    FULL_MOTION,
    gsap,
    ScrollTrigger,
    useGSAP
} from '@/lib/gsap'

import { formatNumber } from '@/lib/format'

const MASK_FROM: Record<string, string> = {
    up: 'inset(100% 0% 0% 0%)',
    down: 'inset(0% 0% 100% 0%)',
    left: 'inset(0% 100% 0% 0%)',
    right: 'inset(0% 0% 0% 100%)',
    center: 'inset(16% 16% 16% 16%)'
}

/** True when an element sits entirely above the viewport (page restored mid-scroll). */
const isAboveViewport = (element: Element): boolean => element.getBoundingClientRect().bottom < 0

/**
 * Page-wide scroll motion driven by data attributes:
 * - data-reveal: fades and rises into view
 * - data-parallax="8": drifts against the scroll inside its frame
 * - data-mask="up|down|left|right|center": the frame wipes open, the image settles
 * - data-count="7048": counts up from zero
 *
 * Call it once per page, after every section has mounted, so pinned
 * sections are measured before anything below them.
 */
export const useScrollMotion = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(() => {
        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            const reveals = gsap.utils.toArray<HTMLElement>('[data-reveal]').filter((element) => !isAboveViewport(element))

            if (reveals.length > 0) {
                gsap.set(reveals, { autoAlpha: 0, y: 40 })

                ScrollTrigger.batch(reveals, {
                    start: 'top 90%',
                    once: true,
                    onEnter: (batch) => {
                        gsap.to(batch, {
                            autoAlpha: 1,
                            y: 0,
                            duration: 1.2,
                            ease: 'expo.out',
                            stagger: 0.09,
                            overwrite: true
                        })
                    }
                })
            }

            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((layer) => {
                const amount = Number(layer.dataset.parallax) || 8

                gsap.fromTo(layer, { yPercent: -amount }, {
                    yPercent: amount,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: layer.parentElement ?? layer,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                })
            })

            gsap.utils.toArray<HTMLElement>('[data-mask]').forEach((frame) => {
                if (isAboveViewport(frame)) return

                const from = MASK_FROM[frame.dataset.mask ?? 'up'] ?? MASK_FROM.up
                const inner = frame.querySelector('[data-mask-inner]')

                const timeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: frame,
                        start: 'top 88%',
                        once: true
                    }
                })

                timeline.fromTo(frame, { clipPath: from }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.5,
                    ease: 'expo.inOut'
                })

                if (inner) {
                    timeline.fromTo(inner, { scale: 1.28 }, {
                        scale: 1,
                        duration: 2,
                        ease: 'expo.out'
                    }, 0.15)
                }
            })

            gsap.utils.toArray<HTMLElement>('[data-count]').forEach((element) => {
                if (isAboveViewport(element)) return

                const target = Number(element.dataset.count) || 0
                const decimals = Number(element.dataset.decimals) || 0
                const proxy = { value: 0 }

                element.textContent = formatNumber(0, decimals)

                gsap.to(proxy, {
                    value: target,
                    duration: 2.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 92%',
                        once: true
                    },
                    onUpdate: () => {
                        element.textContent = formatNumber(proxy.value, decimals)
                    },
                    onComplete: () => {
                        element.textContent = formatNumber(target, decimals)
                    }
                })
            })

            return () => {
                gsap.utils.toArray<HTMLElement>('[data-count]').forEach((element) => {
                    element.textContent = formatNumber(Number(element.dataset.count) || 0, Number(element.dataset.decimals) || 0)
                })
            }
        })

        ScrollTrigger.sort()
        ScrollTrigger.refresh()

        const refresh = (): void => {
            ScrollTrigger.refresh()
        }

        window.addEventListener('load', refresh)
        document.fonts?.ready.then(refresh).catch(() => undefined)

        return () => {
            window.removeEventListener('load', refresh)
            mm.revert()
        }
    }, { scope })
}
